import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  where
} from "firebase/firestore";
import {
  ref,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { imageMimeType, uploadOptimizedImage } from "@/lib/uploadImage";
import { db, storage } from "../firebase";
import { sortByOrder } from "./reorderService";

const GATEWAY_COLLECTION = "getaway";

/**
 * Generates a Firestore-valid doc id without writing anything.
 * Use this once per "new gateway" session so the image upload
 * (which happens before the form is submitted) can be stored
 * under the same id the gateway doc will eventually use.
 * @returns {string}
 */
export const generateGatewayId = () =>
  doc(collection(db, GATEWAY_COLLECTION)).id;

/**
 * @param {string} gatewayId
 * @returns {Promise<import("firebase/firestore").DocumentData|null>}
 */
export const getGateway = async (gatewayId) => {
  const snap = await getDoc(doc(db, GATEWAY_COLLECTION, gatewayId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

/**
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export const getAllgateway = async () => {
  const q = query(
    collection(db, GATEWAY_COLLECTION),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return sortByOrder(items);
};


export const getPackagesByRounds = async () => {
  const q = query(
    collection(db, GATEWAY_COLLECTION),
    where("rounds", "in", [1, 3, 5])
  );

  const snap = await getDocs(q);

  const items = snap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  // Ensure order: 1 round → 3 rounds → 5 rounds
  return items
    .sort((a, b) => Number(a.rounds) - Number(b.rounds))
    .slice(0, 3);
};

/**
 * Creates a gateway at a known id (see generateGatewayId).
 * @param {string} gatewayId
 * @param {Record<string, unknown>} data
 */
export const createGateway = async (gatewayId, data) => {
  await setDoc(doc(db, GATEWAY_COLLECTION, gatewayId), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

// setDoc + merge instead of updateDoc, same reasoning as updateProfile:
// this is also used right after generateGatewayId(), before any doc exists
// (e.g. if the user uploads an image, then abandons and comes back later).
/**
 * @param {string} gatewayId
 * @param {Record<string, unknown>} data
 */
export const updateGateway = async (gatewayId, data) => {
  await setDoc(
    doc(db, GATEWAY_COLLECTION, gatewayId),
    { ...data, updatedAt: serverTimestamp() },
    { merge: true },
  );
};

/**
 * @param {string} gatewayId
 * @param {string} [imageUrl] pass the gateway's current image so its file gets cleaned up too
 */
export const deleteGateway = async (gatewayId, imageUrl) => {
  if (imageUrl) {
    await deleteGatewayImage(gatewayId, imageUrl);
  }
  await deleteDoc(doc(db, GATEWAY_COLLECTION, gatewayId));
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif"];

/**
 * @param {string} gatewayId
 * @param {File} file
 * @param {string} [existingUrl]
 * @returns {Promise<string>}
 */
export const uploadGatewayImage = async (gatewayId, file, existingUrl) => {
  if (!ALLOWED_TYPES.includes(imageMimeType(file))) {
    throw new Error("Only JPEG, PNG, WEBP, or AVIF images are allowed.");
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("Image must be smaller than 5MB.");
  }

  // Clean up the old file first, in case the extension is changing
  if (existingUrl) {
    await deleteGatewayImage(gatewayId, existingUrl);
  }

  const ext = file.name.split(".").pop();
  // timestamped filename so a "replace" doesn't collide with a cached
  // download URL for the old file while the delete above is in flight
  const imageRef = ref(
    storage,
    `gateway_photos/${gatewayId}/hero_${Date.now()}.${ext}`,
  );

  await uploadOptimizedImage(imageRef, file);
  return getDownloadURL(imageRef);
};

/**
 * @param {string} gatewayId
 * @param {string | null | undefined} existingUrl
 */
export const deleteGatewayImage = async (gatewayId, existingUrl) => {
  if (!existingUrl) return;
  try {
    if (existingUrl.includes("firebasestorage")) {
      const imageRef = ref(storage, existingUrl);
      await deleteObject(imageRef);
    }
  } catch (err) {
    console.warn("Could not delete previous gateway image:", err);
  }
};


export const  getAllCourses = async ()=> {
  const q = query(collection(db, "course"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}